<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

const sections = ref([
  {
    id: 'informacion-entidad',
    title: 'Información de la Entidad',
    icon: 'pi pi-info-circle',
    description: 'Misión, visión, estructura orgánica, directorios y más información institucional.',
    route: '/portal-transparencia/informacion-entidad',
    badge: 'Institucional',
    accent: '#008300'
  },
  {
    id: 'normativa',
    title: 'Normativa',
    icon: 'pi pi-book',
    description: 'Leyes, decretos, resoluciones y demás normativa aplicable a la entidad.',
    route: '/portal-transparencia/normativa',
    badge: 'Marco legal',
    accent: '#1d4ed8'
  },
  {
    id: 'contratacion',
    title: 'Contratación',
    icon: 'pi pi-file',
    description: 'Plan anual de adquisiciones, información contractual y ejecución de contratos.',
    route: '/portal-transparencia/contratacion',
    badge: 'Procesos',
    accent: '#b45309'
  },
  {
    id: 'planeacion',
    title: 'Planeación y Presupuesto',
    icon: 'pi pi-chart-line',
    description: 'Presupuesto, planes de acción, proyectos de inversión e informes.',
    route: '/portal-transparencia/planeacion',
    badge: 'Gestión',
    accent: '#7c3aed'
  },
  {
    id: 'tramites',
    title: 'Trámites',
    icon: 'pi pi-check-square',
    description: 'Información sobre trámites y servicios ofrecidos por la entidad.',
    route: '/portal-transparencia/tramites',
    badge: 'Servicios',
    accent: '#0f766e'
  },
  {
    id: 'participa',
    title: 'Participa',
    icon: 'pi pi-users',
    description: 'Mecanismos de participación ciudadana y rendición de cuentas.',
    route: '/portal-transparencia/participa',
    badge: 'Ciudadanía',
    accent: '#be123c'
  },
  {
    id: 'datos-abiertos',
    title: 'Datos Abiertos',
    icon: 'pi pi-database',
    description: 'Instrumentos de gestión de información y datos abiertos.',
    route: '/portal-transparencia/datos-abiertos',
    badge: 'Datos',
    accent: '#0369a1'
  },
  {
    id: 'grupos-interes',
    title: 'Grupos de Interés',
    icon: 'pi pi-heart',
    description: 'Información específica para niños, niñas, adolescentes y mujeres.',
    route: '/portal-transparencia/grupos-interes',
    badge: 'Comunidad',
    accent: '#c2410c'
  }
])

const highlights = ref([
  { label: 'Categorías públicas', value: '10' },
  { label: 'Consulta documental', value: '24/7' },
  { label: 'Acceso ciudadano', value: 'Abierto' }
])

const navigateToSection = (route) => {
  router.push(route)
}
</script>

<template>
  <main class="portal-page">
    <section class="portal-hero">
      <div class="hero-content">
        <router-link to="/" class="institution-link">
          <i class="pi pi-home" aria-hidden="true"></i>
          Institución Educativa JGMM
        </router-link>

        <p class="hero-eyebrow">Información pública y gestión institucional</p>
        <h1>Portal de Transparencia</h1>
        <p class="hero-description">
          Documentos, planes, normativa y canales de participación disponibles para la comunidad educativa.
        </p>

        <div class="hero-actions">
          <button type="button" class="primary-action" @click="navigateToSection('/portal-transparencia/informacion-entidad')">
            <i class="pi pi-search" aria-hidden="true"></i>
            Explorar información
          </button>
          <router-link to="/portal-transparencia/login" class="secondary-action">
            <i class="pi pi-lock" aria-hidden="true"></i>
            Ingreso administrativo
          </router-link>
        </div>
      </div>

      <aside class="hero-panel" aria-label="Resumen del portal">
        <img src="/escudo1-200w.png" alt="Escudo institucional" class="hero-logo" />
        <div class="panel-divider"></div>
        <div class="highlight-list">
          <div v-for="item in highlights" :key="item.label" class="highlight-item">
            <strong>{{ item.value }}</strong>
            <span>{{ item.label }}</span>
          </div>
        </div>
      </aside>
    </section>

    <section class="portal-content">
      <div class="content-heading">
        <div>
          <p class="section-eyebrow">Transparencia activa</p>
          <h2>Secciones del portal</h2>
        </div>
        <p>
          Información organizada según los componentes principales de publicación pública.
        </p>
      </div>

      <div class="sections-grid">
        <button
          v-for="section in sections"
          :key="section.id"
          type="button"
          class="section-card"
          :style="{ '--accent-color': section.accent }"
          @click="navigateToSection(section.route)"
        >
          <span class="card-topline">
            <span class="section-badge">{{ section.badge }}</span>
            <i class="pi pi-arrow-up-right" aria-hidden="true"></i>
          </span>
          <span class="section-icon">
            <i :class="section.icon" aria-hidden="true"></i>
          </span>
          <span class="section-title">{{ section.title }}</span>
          <span class="section-description">{{ section.description }}</span>
        </button>
      </div>
    </section>
  </main>
</template>

<style scoped>
.portal-page {
  min-height: 100vh;
  background:
    linear-gradient(180deg, #f7fbf7 0%, #eef6ef 48%, #ffffff 100%);
  color: #102716;
}

.portal-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 32px;
  align-items: stretch;
  max-width: 1180px;
  margin: 0 auto;
  padding: 48px 24px 32px;
}

.hero-content {
  position: relative;
  overflow: hidden;
  min-height: 360px;
  padding: 44px;
  border-radius: 8px;
  background:
    linear-gradient(135deg, rgba(0, 96, 26, 0.96), rgba(0, 131, 0, 0.86)),
    url('/cole-400h.jpg') center/cover;
  color: #ffffff;
  box-shadow: 0 24px 70px rgba(0, 74, 22, 0.22);
}

.hero-content::after {
  content: '';
  position: absolute;
  right: -80px;
  bottom: -120px;
  width: 320px;
  height: 320px;
  border: 42px solid rgba(255, 255, 255, 0.12);
  border-radius: 50%;
}

.institution-link,
.secondary-action {
  color: inherit;
  text-decoration: none;
}

.institution-link {
  position: relative;
  z-index: 1;
  display: inline-flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 48px;
  font-weight: 800;
}

.hero-eyebrow,
.section-eyebrow {
  margin: 0 0 10px;
  font-size: 0.82rem;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.hero-eyebrow {
  position: relative;
  z-index: 1;
  color: #dff4df;
}

.hero-content h1 {
  position: relative;
  z-index: 1;
  max-width: 680px;
  margin: 0;
  font-size: clamp(2.5rem, 6vw, 5rem);
  line-height: 0.95;
  font-weight: 900;
  letter-spacing: 0;
}

.hero-description {
  position: relative;
  z-index: 1;
  max-width: 680px;
  margin: 22px 0 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.12rem;
  line-height: 1.6;
}

.hero-actions {
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 32px;
}

.primary-action,
.secondary-action {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 8px;
  font-weight: 800;
}

.primary-action {
  border: 0;
  color: #103016;
  background: #ffffff;
  cursor: pointer;
}

.secondary-action {
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.12);
}

.hero-panel {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 32px;
  border: 1px solid rgba(0, 96, 26, 0.13);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 18px 45px rgba(10, 67, 28, 0.12);
}

.hero-logo {
  width: 112px;
  height: 112px;
  object-fit: contain;
  margin: 0 auto;
}

.panel-divider {
  width: 100%;
  height: 1px;
  margin: 30px 0;
  background: #d8e8dc;
}

.highlight-list {
  display: grid;
  gap: 14px;
}

.highlight-item {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid #edf4ef;
}

.highlight-item:last-child {
  border-bottom: 0;
}

.highlight-item strong {
  color: #008300;
  font-size: 1.35rem;
  line-height: 1;
}

.highlight-item span {
  color: #506b58;
  font-size: 0.92rem;
  font-weight: 700;
  text-align: right;
}

.portal-content {
  max-width: 1180px;
  margin: 0 auto;
  padding: 28px 24px 64px;
}

.content-heading {
  display: flex;
  justify-content: space-between;
  gap: 28px;
  align-items: flex-end;
  margin-bottom: 24px;
}

.section-eyebrow {
  color: #008300;
}

.content-heading h2 {
  margin: 0;
  color: #102716;
  font-size: 2rem;
  line-height: 1.1;
  letter-spacing: 0;
}

.content-heading p:last-child {
  max-width: 460px;
  margin: 0;
  color: #5e7465;
  line-height: 1.55;
}

.sections-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}

.section-card {
  min-height: 238px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 20px;
  border: 1px solid #e3ece5;
  border-radius: 8px;
  background: #ffffff;
  color: #102716;
  text-align: left;
  cursor: pointer;
  box-shadow: 0 10px 28px rgba(15, 55, 24, 0.07);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.section-card:hover,
.section-card:focus-visible {
  transform: translateY(-4px);
  border-color: color-mix(in srgb, var(--accent-color) 42%, #ffffff);
  box-shadow: 0 18px 42px rgba(15, 55, 24, 0.14);
  outline: none;
}

.card-topline {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin-bottom: 18px;
}

.section-badge {
  padding: 6px 9px;
  border-radius: 999px;
  color: var(--accent-color);
  background: color-mix(in srgb, var(--accent-color) 12%, #ffffff);
  font-size: 0.75rem;
  font-weight: 900;
}

.card-topline i {
  color: var(--accent-color);
}

.section-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: 18px;
  border-radius: 8px;
  color: #ffffff;
  background: var(--accent-color);
  font-size: 1.25rem;
}

.section-title {
  color: #102716;
  font-size: 1.08rem;
  line-height: 1.25;
  font-weight: 900;
}

.section-description {
  margin-top: 10px;
  color: #637768;
  font-size: 0.95rem;
  line-height: 1.5;
}

@media (max-width: 1100px) {
  .portal-hero {
    grid-template-columns: 1fr;
  }

  .hero-panel {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 24px;
    align-items: center;
  }

  .panel-divider {
    display: none;
  }

  .sections-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 700px) {
  .portal-hero,
  .portal-content {
    padding-left: 16px;
    padding-right: 16px;
  }

  .hero-content {
    min-height: auto;
    padding: 30px 22px;
  }

  .institution-link {
    margin-bottom: 34px;
  }

  .hero-actions,
  .content-heading {
    align-items: stretch;
    flex-direction: column;
  }

  .primary-action,
  .secondary-action {
    width: 100%;
  }

  .hero-panel {
    grid-template-columns: 1fr;
  }

  .hero-logo {
    margin: 0;
  }

  .highlight-item span {
    text-align: left;
  }

  .sections-grid {
    grid-template-columns: 1fr;
  }
}
</style>
